<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue';
import {
  FwbSidebarItemGroup,
  FwbTooltip
} from 'flowbite-vue';

import { SIDE_MENU_PRODUCTS, type APP } from '@base-shared/utils/constants';
import { useSideNavCollapse } from '@base-shared/composables/use-sidenav-collapse';

import SideMenuItem from '@base-shared/components/SideMenuItem.vue';
import SideMenuDropdownItem from '@base-shared/components/SideMenuDropdownItem.vue';
import ProductSelectNav from '@base-shared/components/ProductSelectNav.vue';
import IconSvg from '@base-shared/components/IconSvg.vue';

import type { NavItem, NavMenuItemGroup, NavGroup } from '@base-shared/types/navigation';

type SideMenuDropdownItemInstance = InstanceType<typeof SideMenuDropdownItem>;

/** Use composable to manage side nav collapse state */
const { isSideNavCollapsed, userToggle } = useSideNavCollapse();

const props = defineProps<{
  menuData: {
    groups: NavGroup[];
    activeRouteName?: string;
  };

  product?: APP;
  isVisible: boolean;
  showProductsNav: boolean;

  userRights?: string[];
  userEnabledProducts?: string[];
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'onBackToApp'): void;
  (e: 'itemSelect', item: NavItem): void;
  (e: 'productSelect', product: string): void;
}>();

/**
 * Element refs equivalent to {@link NavMenuItemGroup}
 * rendered using {@link SideMenuDropdownItemInstance}.
 */
const navMenuItemGroupRefs = ref<Record<string, SideMenuDropdownItemInstance | null>>({});

/**
 * Currently active/opened group ID dropdown.
 * @see {@link SideMenuDropdownItem}
 */
const activeGroupIdDropdown = ref<string | null>(null);

/** Selected product based on `product` prop */
const selectedProduct = computed(() => SIDE_MENU_PRODUCTS.find((product) => product.value === props.product));

/** Map selected menu item group {@link NavMenuItemGroup} */
const selectedMenuItemGroup = computed(() => {
  if (!props.menuData?.groups?.length || !props.menuData?.activeRouteName) {
    return null;
  }

  const activeRouteName = props.menuData?.activeRouteName;
  for (const group of props.menuData.groups) {
    const groupItem = group.items.find((item) => {
      if (item.subMenuItems?.length) {
        return item.subMenuItems?.some((subItem) => subItem.routeName?.includes(activeRouteName));
      }

      return item.routeName?.includes(activeRouteName);
    });

    if (groupItem) {
      return groupItem as NavMenuItemGroup;
    }
  }

  return null;
});

/**
 * Update elements active/inactive state upon different events
 * including groups list changes
 */
watch(
  [
    isSideNavCollapsed,
    () => props.menuData,
    () => props.showProductsNav
  ],
  async () => {
    await toggleActiveInactiveMenuItemGroups();
  }, {
    deep: true,
    immediate: true
  }
);

/**
 * Close all sub menu dropdowns when the side menu collapses
 */
function closeAllSubMenuDropdowns() {
  if (!navMenuItemGroupRefs.value) {
    return;
  }

  for (const id in navMenuItemGroupRefs.value) {
    navMenuItemGroupRefs.value[id]?.toggleDropdown?.(false);
  }
}

/**
 * Close inactive menu item group dropdowns
 */
function closeInactiveMenuItemGroups() {
  if (!selectedMenuItemGroup.value?.id || !navMenuItemGroupRefs.value) {
    return;
  }

  const selectedId = selectedMenuItemGroup.value?.id;

  for (const id in navMenuItemGroupRefs.value) {
    if (id === selectedId) {
      continue;
    }

    navMenuItemGroupRefs.value?.[id]?.toggleDropdown?.(false);
  }
}

/**
 * Opens selected menu item group dropdown on init or DOM change
 * Useful for showing selected menu items inside the dropdown
 */
function openActiveMenuItemGroup() {
  if (!selectedMenuItemGroup.value?.id || !navMenuItemGroupRefs.value) {
    return;
  }

  const selectedId = selectedMenuItemGroup.value.id;
  if (navMenuItemGroupRefs.value[selectedId]) {
    navMenuItemGroupRefs.value[selectedId].toggleDropdown(true);
  }
}

/**
 * Toggle active/inactive menu item groups.
 */
async function toggleActiveInactiveMenuItemGroups() {
  // If side nav is collapsed or product select nav is
  // visible, just close all sub menu dropdowns
  if (isSideNavCollapsed.value || props.showProductsNav) {
    closeAllSubMenuDropdowns();
    return;
  }

  // Close all inactive menu item groups initially
  await nextTick();
  closeInactiveMenuItemGroups();

  // Then open active menu item group
  await nextTick();
  openActiveMenuItemGroup();
}

/**
 * Close product selection nav
 */
async function backToApp() {
  emit('onBackToApp');

  // Need to wait for the emit and DOM updates to finish
  await nextTick();
  if (!props.showProductsNav && !isSideNavCollapsed) {
    openActiveMenuItemGroup();
  }
}

function onItemSelect(item: NavItem) {
  if (!item) return;

  emit('itemSelect', item);
}

function onProductSelect(product: string) {
  if (!product) return;

  emit('productSelect', product);
}

defineExpose({
  toggleActiveInactiveMenuItemGroups
});
</script>

<template>
  <aside
    v-if="isVisible"
    class="relative side-nav-container w-64 transition-transform group flex flex-col"
    :class="{ 'w-[60px]!': isSideNavCollapsed && !showProductsNav }"
    data-component-element-id="side-nav-container"
  >
    <!-- Nav Header -->
    <slot name="nav-header" />

    <!-- Toggle Button -->
    <button
      v-if="!showProductsNav"
      class="absolute right-[-16px] top-1/2 z-20 bg-gray-300 rounded-full transition-opacity opacity-0 group-hover:opacity-100 hover:bg-gray-400"
      data-component-element-id="side-nav-toggle-btn"
      @click="userToggle"
    >
      <IconSvg
        :name="isSideNavCollapsed ? 'flowbite:chevron-right-outline' : 'flowbite:chevron-left-outline'"
        height-size="sm"
        width-size="sm"
      />
    </button>
    <div
      class="side-nav h-full p-3 overflow-y-auto flex flex-col flex-1"
      :class="[{ 'px-0': isSideNavCollapsed && !showProductsNav }, product]"
    >
      <!-- Nav Items -->
      <template v-if="!showProductsNav">
        <div class="flex flex-col flex-1">
          <template
            v-for="group in menuData.groups"
            :key="group.id"
          >
            <div
              v-if="group.hasSpacerTop"
              class="flex-1"
            />

            <slot
              :name="`${group.id}-before`"
              :group="group"
              :active-group-id-dropdown="activeGroupIdDropdown"
            />

            <FwbSidebarItemGroup
              :border="false"
              class="flex flex-col gap-2 pb-2"
              :data-component-element-id="`side-nav-group-${group.id}`"
              :class="{
                'border-b border-gray-200 mb-4 pb-4!': group.hasBorder,
              }"
            >
              <template
                v-for="item in group.items"
                :key="item.id"
              >
                <!-- SideMenuNav with SubMenu -->
                <SideMenuDropdownItem
                  v-if="item?.subMenuItems && item.subMenuItems?.length > 0"
                  :ref="(el) => { navMenuItemGroupRefs[item.id] = el as SideMenuDropdownItemInstance | null }"
                  class="side-nav-sub-menu"
                  :class="{
                    'selected-parent': selectedMenuItemGroup && item.id === selectedMenuItemGroup?.id,
                  }"
                  :data-component-element-id="`side-nav-sub-menu-${item.id}`"
                  :item-id="item.id"
                  :inverse-animation="item.inverseAnimation"
                  @dropdown-toggle="activeGroupIdDropdown = $event ? group.id : null"
                >
                  <!-- Icon -->
                  <template #icon>
                    <IconSvg
                      v-if="item.icon"
                      height-size="xs"
                      :name="item.icon"
                    />
                  </template>

                  <!-- Name -->
                  <template #trigger>
                    {{ $t(item.name) }}
                  </template>

                  <!-- Sub Menu Items -->
                  <template #default>
                    <SideMenuItem
                      v-for="subItem in item.subMenuItems"
                      :key="subItem.id"
                      class="side-nav-sub-menu-item pl-11"
                      v-bind="subItem.fullPath ? { link: subItem.fullPath } : ''"
                      :data-component-element-id="`side-nav-sub-menu-item-${subItem.id}`"
                      :tag="subItem.fullPath ? 'router-link' : 'span'"
                      :active-route-name="menuData?.activeRouteName"
                      :nav-item="subItem"
                      @click="onItemSelect(subItem)"
                    >
                      {{ $t(subItem.name) }}
                    </SideMenuItem>
                  </template>
                </SideMenuDropdownItem>

                <!-- SideMenuNav Item without SubMenu -->
                <template v-else>
                  <!-- For expanded view, show icon and name as default -->
                  <SideMenuItem
                    v-if="!isSideNavCollapsed"
                    class="side-nav-menu-item"
                    :data-component-element-id="`side-nav-menu-item-${item.id}`"
                    :link="item.fullPath"
                    :tag="item.fullPath ? 'router-link' : 'a'"
                    :active-route-name="menuData?.activeRouteName"
                    :nav-item="item"
                    @click="onItemSelect(item)"
                  >
                    <!-- Icon -->
                    <template #icon>
                      <IconSvg
                        v-if="item.icon"
                        height-size="xs"
                        :name="item.icon"
                      />
                    </template>

                    <!-- Name -->
                    <template #default>
                      {{ $t(item.name) }}
                    </template>
                  </SideMenuItem>

                  <!-- For collapsed view, display icon with tooltip as default -->
                  <FwbTooltip
                    v-else
                    placement="right"
                  >
                    <template #trigger>
                      <SideMenuItem
                        class="side-nav-menu-item mx-2.5"
                        :data-component-element-id="`side-nav-menu-item-${item.id}`"
                        :link="item.fullPath"
                        :tag="item.fullPath ? 'router-link' : 'a'"
                        :active-route-name="menuData?.activeRouteName"
                        :nav-item="item"
                        @click="onItemSelect(item)"
                      >
                        <div class="flex items-center justify-center">
                          <IconSvg
                            v-if="item.icon"
                            height-size="xs"
                            :name="item.icon"
                          />
                        </div>
                      </SideMenuItem>
                    </template>
                    <template #content>
                      {{ $t(item.name) }}
                    </template>
                  </FwbTooltip>
                </template>
              </template>
            </FwbSidebarItemGroup>

            <slot
              :name="`${group.id}-after`"
              :group="group"
              :active-group-id-dropdown="activeGroupIdDropdown"
            />
          </template>
        </div>

        <!-- Footer -->
        <slot name="footer" />
      </template>

      <!-- Select Products -->
      <template v-else>
        <div
          v-if="selectedProduct"
          class="flex items-center font-medium text-gray-300 cursor-pointer gap-2 px-2"
          data-component-element-id="product-select-nav-back-btn"
          @click="backToApp"
        >
          <IconSvg
            name="flowbite:arrow-left-outline"
            width-size="xs"
            height-size="xs"
          />
          <span>{{ $t('buttons.backToProduct', { productName: selectedProduct?.name ?? '' }) }}</span>
        </div>

        <ProductSelectNav
          class="flex-1"
          :product="product"
          :user-enabled-products="userEnabledProducts"
          :user-rights="userRights"
          :is-loading="isLoading"
          data-component-element-id="product-select-nav"
          @product-select="onProductSelect"
        >
          <template #no-products-placeholder>
            <slot name="no-products-placeholder" />
          </template>
        </ProductSelectNav>

        <!-- Footer -->
        <slot name="footer" />
      </template>
    </div>
    <!-- Nav Footer -->
    <slot name="nav-footer" />
  </aside>
</template>

<style lang="css" scoped>
@reference '@base-shared/assets/css/tailwind.css';

.side-nav-container {
  .side-nav {
    :deep(.side-nav-menu-item) {
      /* Add truncate to side nav items */
      & > span {
        @apply truncate;
      }
    }

    :deep(.side-nav-sub-menu) {
      .side-nav-sub-menu-item {
        /* Add truncate to side nav sub menu items */
        & > span {
          @apply truncate;
        }
      }
    }

    &.hub-settings, &.arya {
      :deep(.side-nav-menu-item) {
        @apply text-gray-300 hover:text-spinetix-900 hover:bg-white cursor-pointer;
      }

      :deep(.side-nav-menu-item.router-link-exact-active),
      :deep(.side-nav-menu-item.router-link-sub-route-active) {
        @apply bg-white text-spinetix-900;
      }

      :deep(.side-nav-sub-menu) {
        &.selected-parent {
          & > .fwb-tooltip > .v-popper > button {
            @apply bg-white! text-spinetix-900!;
          }
        }

        & > button,
        & > .fwb-tooltip > .v-popper > button {
          @apply text-gray-300 hover:text-spinetix-900 hover:bg-white;
        }

        & > div > div {
          @apply flex flex-col gap-2;
        }

        .side-nav-sub-menu-item {
          @apply text-gray-300 hover:text-spinetix-900 hover:bg-white cursor-pointer;

          &.router-link-exact-active {
            @apply bg-white text-spinetix-900;
          }
        }
      }
    }

    &.assets {
      :deep(.side-nav-menu-item) {
        @apply text-gray-300 hover:text-spinetix-900 hover:bg-assets-500 cursor-pointer;
      }

      :deep(.side-nav-menu-item.router-link-exact-active),
      :deep(.side-nav-menu-item.router-link-sub-route-active) {
        @apply bg-assets-500 text-spinetix-900;
      }

      :deep(.side-nav-sub-menu) {
        &.selected-parent {
          & > .fwb-tooltip > .v-popper > button {
            @apply bg-assets-500! text-spinetix-900!;
          }
        }

        & > button,
        & > .fwb-tooltip > .v-popper > button {
          @apply text-gray-300 hover:text-spinetix-900 hover:bg-assets-500;
        }

        & > .fwb-tooltip > .v-popper {
          @apply w-full;
        }

        & > div > div {
          @apply flex flex-col gap-2;
        }

        .side-nav-sub-menu-item {
          @apply text-gray-300 hover:text-spinetix-900 hover:bg-assets-500 cursor-pointer;

          &.router-link-exact-active {
            @apply bg-assets-500 text-spinetix-900;
          }
        }
      }
    }

    &.integrations {
      :deep(.side-nav-menu-item) {
        @apply text-gray-300 hover:text-spinetix-900 hover:bg-integrations-500 cursor-pointer;
      }

      :deep(.side-nav-menu-item.router-link-exact-active),
      :deep(.side-nav-menu-item.router-link-sub-route-active) {
        @apply bg-integrations-500 text-spinetix-900;
      }

      :deep(.side-nav-sub-menu) {
        &.selected-parent {
          & > .fwb-tooltip > .v-popper > button {
            @apply bg-integrations-500! text-spinetix-900!;
          }
        }

        & > button,
        & > .fwb-tooltip > .v-popper > button {
          @apply text-gray-300 hover:text-spinetix-900 hover:bg-integrations-500;
        }

        & > div > div {
          @apply flex flex-col gap-2;
        }

        .side-nav-sub-menu-item {
          @apply text-gray-300 hover:text-spinetix-900 hover:bg-integrations-500 cursor-pointer;

          &.router-link-exact-active {
            @apply bg-integrations-500 text-spinetix-900;
          }
        }
      }
    }
  }
}
</style>
